
import { Component, Vue } from "vue-property-decorator"
import { DimssaButton, ButtonState } from "@/components/dimssa-button.vue"
import { ConfigsLocation, runGetLocations } from "@/helpers/configs"
import * as Models from "@gigalot/data-models"
import { getGcp } from "@gigalot/utils"
import { mqttDeviceId } from "@/helpers/mqtt"
import { downloadObjectAsJson } from "@/helpers/download"
import { camelCase } from "@/helpers"
import {
  configFileFeeder as generateConfigFileFeeder,
  configFileOffice as generateConfigFileOffice,
  configFileProxyGantryScanner as generateConfigFileProxyGantryScanner,
  configFileProxyHandHeldScanner as generateConfigFileProxyHandHeldScanner,
  configFileProxyProcessingStation as generateConfigFileProxyProcessingStation,
} from "@/helpers/config-generator"

@Component({
  components: {
    DimssaButton,
  },
})
export default class Configs extends Vue {
  locations: ConfigsLocation[] = []
  holdingCompanies: Models.HoldingCompany[] = []
  selectedLocation: ConfigsLocation | null = null
  search: string = ""
  getLocationsButtonState: ButtonState = "ready"

  get filteredLocations(): ConfigsLocation[] {
    const search = this.search.trim().toLowerCase()
    if (!search) {
      return this.locations
    }

    return this.locations.filter((location) => location.name.toLowerCase().includes(search))
  }

  get isMobile() {
    return this.$vuetify.breakpoint.smAndDown
  }

  async getLocations() {
    await runGetLocations({
      setButtonState: (state) => {
        this.getLocationsButtonState = state as ButtonState
      },
      setLocations: (locations) => {
        this.locations = locations
      },
      setHoldingCompanies: (holdingCompanies) => {
        this.holdingCompanies = holdingCompanies
      },
    })
  }

  getProcessingConfigFilename(processingStation: Models.ProcessingStation, feedlot: Models.Feedlot, holdingCompany: Models.HoldingCompany) {
    let filename = `config_ProcessingStation-${camelCase(processingStation.description)}`
    filename += `_${feedlot.typename}-${camelCase(feedlot.name)}`
    filename += `_${holdingCompany.typename}-${camelCase(holdingCompany.name)}`
    return filename
  }

  configFileProcessingStation(processingStation: Models.ProcessingStation, download = true) {
    if (!this.selectedLocation) {
      return
    }

    const { feedlot, holdingCompany } = this.selectedLocation
    const filename = this.getProcessingConfigFilename(processingStation, feedlot, holdingCompany)

    const json = {
      processingStation: {
        guid: processingStation.guid,
        description: processingStation.description,
      },
      location: {
        type: feedlot.typename,
        name: feedlot.name,
        guid: feedlot.guid,
      },
      hardware: {
        allflex: true,
      },
      gates: processingStation.sortingGates,
    }

    if (download) downloadObjectAsJson(json, filename)
    return json
  }

  configFileOffice(download = true) {
    if (!this.selectedLocation) {
      return
    }

    const { feedlot, holdingCompany } = this.selectedLocation
    const filename = `config_Feedlot-${camelCase(feedlot.name)}_${holdingCompany.typename}-${camelCase(holdingCompany.name)}`
    const json = generateConfigFileOffice({ feedlot, holdingCompany })

    if (download) downloadObjectAsJson(json, filename)
    return json
  }

  configFileFeeder(download = true) {
    if (!this.selectedLocation) {
      return
    }

    const { feedlot, holdingCompany } = this.selectedLocation
    const filename = `config_Feeder-${camelCase(feedlot.name)}_${holdingCompany.typename}-${camelCase(holdingCompany.name)}`
    const json = generateConfigFileFeeder({ feedlot, holdingCompany })

    if (download) downloadObjectAsJson(json, filename, { escapeMongoUrlSlashes: true })
    return json
  }

  configFileProxyProcessingStation(processingStation: Models.ProcessingStation, download = true) {
    if (!this.selectedLocation) {
      return
    }

    const { feedlot, holdingCompany } = this.selectedLocation
    const json = generateConfigFileProxyProcessingStation({
      mqttDeviceId: this.mqttDeviceIdProcessingStation(processingStation, feedlot, holdingCompany),
      holdingCompanies: this.holdingCompanies,
    })

    if (download) downloadObjectAsJson(json, "config")
    return json
  }

  configFileProxyGantryScanner(gantryScanner: Models.GantryScanner, download = true) {
    if (!this.selectedLocation) {
      return
    }

    const { feedlot, holdingCompany } = this.selectedLocation
    const json = generateConfigFileProxyGantryScanner({
      mqttDeviceId: this.mqttDeviceIdGantryScanner(gantryScanner, feedlot, holdingCompany),
    })

    if (download) downloadObjectAsJson(json, "config")
    return json
  }

  configFileProxyHandHeldScanner(handHeldScanner: Models.HandHeldScanner, download = true) {
    if (!this.selectedLocation) {
      return
    }

    const { feedlot, holdingCompany } = this.selectedLocation
    const json = generateConfigFileProxyHandHeldScanner({
      mqttDeviceId: this.mqttDeviceIdHandHeldScanner(handHeldScanner, feedlot, holdingCompany),
    })

    if (download) downloadObjectAsJson(json, "config")
    return json
  }

  mqttDeviceIdProcessingStation(processingStation: Models.ProcessingStation, feedlot: Models.Feedlot, holdingCompany: Models.HoldingCompany) {
    const what: string = "processing-station"
    const processingStations: Models.ProcessingStation[] = feedlot.processingStations
    const index = processingStations.findIndex((pr) => pr.guid === processingStation.guid)
    let num = index + 1
    if (index < 0) {
      //user might be busy with a new processing station that has not been saved yet.
      num = processingStations.length + 1
    }
    return mqttDeviceId(what, this.getGcp(feedlot, holdingCompany), this.getGuid(feedlot), num)
  }

  mqttDeviceIdGantryScanner(gantryScanner: Models.GantryScanner, feedlot: Models.Feedlot, holdingCompany: Models.HoldingCompany) {
    const what: string = "gantry-scanner"
    const gantryScanners: Models.GantryScanner[] = feedlot.gantryScanners
    const index = gantryScanners.findIndex((gs) => gs.guid === gantryScanner.guid)
    let num = index + 1
    if (index < 0) {
      num = gantryScanners.length + 1
    }
    return mqttDeviceId(what, this.getGcp(feedlot, holdingCompany), this.getGuid(feedlot), num)
  }

  mqttDeviceIdHandHeldScanner(handHeldScanner: Models.HandHeldScanner, feedlot: Models.Feedlot, holdingCompany: Models.HoldingCompany) {
    const what: string = "hand-held-scanner"
    const handHeldScanners: Models.HandHeldScanner[] = feedlot.handHeldScanners
    const index = handHeldScanners.findIndex((gs) => gs.guid === handHeldScanner.guid)
    let num = index + 1
    if (index < 0) {
      num = handHeldScanners.length + 1
    }
    return mqttDeviceId(what, this.getGcp(feedlot, holdingCompany), this.getGuid(feedlot), num)
  }

  getGuid(feedlot: Models.Feedlot) {
    return feedlot.guid
  }

  getGcp(feedlot: Models.Feedlot, holdingCompany: Models.HoldingCompany) {
    return getGcp(feedlot, holdingCompany)
  }
}
